import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { buildQuoteEmailDraft, gmailComposeUrl } from "@/lib/outreach";
import { Mail, Plus, Printer, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

const UNITS = ["reams", "boxes", "rolls", "kg", "MT", "sheets", "units", "pcs"];

const emptyItem = () => ({ product: "", unit: "reams", quantity: 1, cif_price: 0, fob_price: 0, ex_works_price: 0 });

export default function QuotationBuilder({ customer, deal, existingQuote, onClose, onSaved }) {
  const { user } = useAuth();
  const isEdit = !!existingQuote;
  const [currencies, setCurrencies] = useState([]);
  const [products, setProducts] = useState([]);
  const [currencyCode, setCurrencyCode] = useState(existingQuote?.currency || "INR");
  const [fixedTerms, setFixedTerms] = useState(existingQuote?.fixed_terms || []);
  const [items, setItems] = useState(
    existingQuote?.items?.length
      ? existingQuote.items
      : [{ ...emptyItem(), product: deal?.requirements || "" }]
  );
  const [extraNotes, setExtraNotes] = useState(existingQuote?.notes || "");
  const [paymentTerms, setPaymentTerms] = useState(existingQuote?.payment_terms || "");
  const [showExworks, setShowExworks] = useState(existingQuote?.show_exworks ?? true);
  const [date, setDate] = useState(existingQuote?.date || new Date().toISOString().split("T")[0]);
  const [validUntil, setValidUntil] = useState(() => {
    if (existingQuote?.valid_until) return existingQuote.valid_until;
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split("T")[0];
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/currencies").then((r) => setCurrencies(r.data)),
      ...(isEdit ? [] : [api.get("/default-terms").then((r) => setFixedTerms(r.data.terms))]),
      api.get("/products").then((r) => setProducts(r.data)),
    ]).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currency = currencies.find((c) => c.code === currencyCode) || { symbol: "₹" };
  const sym = currency.symbol;

  const addItem = () => setItems((i) => [...i, emptyItem()]);
  const removeItem = (idx) => setItems((i) => i.filter((_, j) => j !== idx));
  const updateItem = (idx, key, val) => setItems((i) => i.map((r, j) => (j === idx ? { ...r, [key]: val } : r)));

  const num = (v) => parseFloat(v) || 0;
  const totalFor = (priceKey) => items.reduce((s, r) => s + num(r[priceKey]) * num(r.quantity), 0);
  const cifTotal = totalFor("cif_price");
  const fobTotal = totalFor("fob_price");
  const exwTotal = totalFor("ex_works_price");
  const taxRate = currency.tax_rate || 0;
  const taxLabel = currency.tax_label || "";
  const taxAmount = fobTotal * taxRate;
  const grandTotal = fobTotal + taxAmount;

  const fmtMoney = (n) => sym + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const removeFixedTerm = (idx) => setFixedTerms((t) => t.filter((_, j) => j !== idx));

  const persist = async () => {
    if (!isEdit && !deal?.id) {
      toast.error("A quotation must be created from a deal.");
      return null;
    }
    const mappedItems = items.map((i) => ({
      product: i.product,
      unit: i.unit,
      quantity: num(i.quantity),
      cif_price: num(i.cif_price),
      fob_price: num(i.fob_price),
      ex_works_price: num(i.ex_works_price),
    }));
    if (isEdit) {
      const res = await api.put(`/quotations/${existingQuote.id}`, {
        items: mappedItems,
        notes: extraNotes,
        fixed_terms: fixedTerms,
        payment_terms: paymentTerms,
        date,
        valid_until: validUntil,
        currency: currencyCode,
        show_exworks: showExworks,
      });
      toast.success(`Quotation ${res.data.quote_no} updated`);
      return res.data;
    } else {
      const res = await api.post("/quotations", {
        customer_id: customer.id,
        deal_id: deal?.id,
        items: mappedItems,
        notes: extraNotes,
        fixed_terms: fixedTerms,
        payment_terms: paymentTerms,
        date,
        valid_until: validUntil,
        currency: currencyCode,
        show_exworks: showExworks,
        status: "draft",
      });
      toast.success(`Quotation ${res.data.quote_no} saved`);
      return res.data;
    }
  };

  const saveOnly = async () => {
    setSaving(true);
    try {
      const data = await persist();
      if (data) onSaved && onSaved(data);
    } catch (e) {
      toast.error(isEdit ? "Failed to update quotation" : "Failed to save quotation");
    } finally {
      setSaving(false);
    }
  };

  const saveAndPrint = async () => {
    setSaving(true);
    try {
      const data = await persist();
      if (data) {
        onSaved && onSaved(data);
        openPrintWindow(data);
      }
    } catch (e) {
      toast.error(isEdit ? "Failed to update quotation" : "Failed to save quotation");
    } finally {
      setSaving(false);
    }
  };

  const saveAndEmail = async () => {
    setSaving(true);
    try {
      const data = await persist();
      if (data) {
        onSaved && onSaved(data);
        const draft = buildQuoteEmailDraft(data, customer, user);
        window.open(gmailComposeUrl(draft), "_blank", "noopener,noreferrer");
        toast.success("Opening Gmail…");
      }
    } catch (e) {
      toast.error(isEdit ? "Failed to update quotation" : "Failed to save quotation");
    } finally {
      setSaving(false);
    }
  };

  const openPrintWindow = (q) => {
    const win = window.open("", "_blank", "width=900,height=720");
    const fmt = (n) => q.currency_symbol + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const dateStr = new Date(q.date || Date.now()).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    const validStr = new Date(q.valid_until).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    const allTermsHtml = [
      ...(q.fixed_terms || []).map((t) => `<li>${t}</li>`),
      ...(q.notes ? q.notes.split("\n").filter(Boolean).map((t) => `<li>${t}</li>`) : []),
    ].join("");
    const showExw = q.show_exworks !== false;
    const hasTax = (q.tax_rate || 0) > 0;
    win.document.write(`<!DOCTYPE html><html><head><title>Quotation ${q.quote_no}</title>
      <link href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet">
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Manrope', sans-serif; color: #1C1B1A; background: #F9F8F6; padding: 32px; }
        .doc { max-width: 880px; margin: 0 auto; background: #fff; border: 1px solid #E5E0D8; }
        .head { padding: 32px 40px; border-bottom: 1px solid #E5E0D8; display: flex; justify-content: space-between; align-items: flex-start; }
        .brand { font-weight: 800; font-size: 24px; letter-spacing: -0.02em; }
        .tagline { font-size: 11px; color: #6B6965; margin-top: 4px; text-transform: uppercase; letter-spacing: 0.18em; }
        .badge { text-align: right; }
        .badge .lbl { font-size: 10px; letter-spacing: 0.22em; color: #6B6965; text-transform: uppercase; }
        .badge .num { font-family: 'JetBrains Mono', monospace; font-size: 22px; font-weight: 700; color: #C85A17; }
        .meta { padding: 18px 40px; display: flex; gap: 48px; border-bottom: 1px solid #E5E0D8; background: #F2EFEA; }
        .meta .lbl { font-size: 10px; letter-spacing: 0.22em; color: #6B6965; text-transform: uppercase; }
        .meta .val { font-family: 'JetBrains Mono', monospace; font-size: 13px; margin-top: 4px; }
        .bill { padding: 24px 40px; border-bottom: 1px solid #E5E0D8; display:flex; justify-content:space-between; gap:32px; }
        .bill .lbl { font-size: 10px; letter-spacing: 0.22em; color: #C85A17; text-transform: uppercase; font-weight: 700; }
        .bill .co { font-size: 16px; font-weight: 700; margin-top: 6px; }
        .bill .det { font-size: 13px; color: #6B6965; margin-top: 2px; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #F9F8F6; font-size: 10px; letter-spacing: 0.16em; text-transform: uppercase; color: #6B6965; font-weight: 700; padding: 12px 14px; text-align: left; border-bottom: 1px solid #E5E0D8; }
        th:nth-child(3), th:nth-child(4), th:nth-child(5), th:nth-child(6) { text-align: right; }
        td { padding: 12px 14px; font-size: 13px; border-bottom: 1px solid #F2EFEA; vertical-align: top; }
        td:nth-child(3), td:nth-child(4), td:nth-child(5), td:nth-child(6) { text-align: right; font-family: 'JetBrains Mono', monospace; }
        .totals { padding: 20px 40px; display: flex; justify-content: flex-end; gap: 32px; border-bottom: 1px solid #E5E0D8; }
        .totals .box { text-align: right; }
        .totals .lbl { font-size: 10px; letter-spacing: 0.18em; color: #6B6965; text-transform: uppercase; }
        .totals .v { font-family: 'JetBrains Mono', monospace; font-size: 16px; font-weight: 700; margin-top: 4px; }
        .totals .v.fob { color: #C85A17; }
        .notes { padding: 20px 40px; background: #FFF0E6; border-bottom: 1px solid #E5E0D8; font-size: 12px; color: #6B6965; }
        .notes strong { color: #C85A17; display: block; font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase; margin-bottom: 8px; }
        .notes ol { padding-left: 18px; margin-top: 4px; }
        .notes li { margin-bottom: 4px; line-height: 1.5; }
        .foot { text-align: center; padding: 18px; font-size: 11px; color: #6B6965; }
        /* margin:0 on the page suppresses the browser's own header/footer
           (the "about:blank" URL and print date/time). Inner spacing is
           restored via body padding so the document keeps a print margin. */
        @page { margin: 0; }
        @media print { body { padding: 12mm; background: #fff; } .doc { border: none; } }
      </style></head><body>
      <div class="doc">
        <div class="head">
          <div>
            <div class="brand">Zalani Paper Mart</div>
            <div class="tagline">Premium Paper & Packaging</div>
          </div>
          <div class="badge">
            <div class="lbl">Quotation</div>
            <div class="num">${q.quote_no}</div>
          </div>
        </div>
        <div class="meta">
          <div><div class="lbl">Date</div><div class="val">${dateStr}</div></div>
          <div><div class="lbl">Valid Until</div><div class="val">${validStr}</div></div>
          <div><div class="lbl">Currency</div><div class="val">${q.currency}</div></div>
          ${q.created_by_name ? `<div><div class="lbl">Prepared By</div><div class="val">${q.created_by_name}</div></div>` : ""}
        </div>
        <div class="bill">
          <div>
            <div class="lbl">Bill To</div>
            <div class="co">${customer.company}</div>
            ${customer.contact ? `<div class="det">${customer.contact}</div>` : ""}
            ${(customer.emails && customer.emails[0]) ? `<div class="det">${customer.emails[0]}</div>` : ""}
            ${customer.country ? `<div class="det">${customer.country}</div>` : ""}
          </div>
        </div>
        <table>
          <thead><tr><th>#</th><th>Product</th><th>Qty</th><th>FOB</th><th>CIF</th>${showExw ? "<th>Ex-Works</th>" : ""}</tr></thead>
          <tbody>
            ${q.items.map((r, i) => `<tr><td>${i + 1}</td><td>${r.product || "—"}</td><td>${r.quantity} ${r.unit || ""}</td><td>${fmt(r.fob_price)}</td><td>${fmt(r.cif_price)}</td>${showExw ? `<td>${fmt(r.ex_works_price)}</td>` : ""}</tr>`).join("")}
          </tbody>
        </table>
        <div class="totals">
          <div class="box"><div class="lbl">FOB Total</div><div class="v${hasTax ? "" : " fob"}">${fmt(q.fob_total)}</div></div>
          <div class="box"><div class="lbl">CIF Total</div><div class="v">${fmt(q.cif_total)}</div></div>
          ${showExw ? `<div class="box"><div class="lbl">Ex-Works Total</div><div class="v">${fmt(q.exworks_total)}</div></div>` : ""}
          ${hasTax ? `<div class="box"><div class="lbl">${q.tax_label || "GST"}</div><div class="v">${fmt(q.tax_amount)}</div></div>` : ""}
          ${hasTax ? `<div class="box"><div class="lbl">Grand Total</div><div class="v fob">${fmt(q.grand_total)}</div></div>` : ""}
        </div>
        ${q.payment_terms ? `<div class="notes"><strong>Payment Terms</strong><div style="font-size:13px;color:#1C1B1A;white-space:pre-wrap">${q.payment_terms}</div></div>` : ""}
        ${allTermsHtml ? `<div class="notes"><strong>Terms & Conditions</strong><ol>${allTermsHtml}</ol></div>` : ""}
        <div class="foot">Zalani Paper Mart · sales@zalanipaper.com · Thank you for your business</div>
      </div>
      </body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  const cellInput = { border: "none", padding: 4, background: "transparent" };

  return (
    <div data-testid="quotation-builder">
      {/* Bill-to + Currency + Date + Valid */}
      <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: "1.4fr 1fr 1fr 1fr" }}>
        <div className="card-ledger" style={{ padding: 16, background: "var(--surface-2)" }}>
          <div className="overline" style={{ fontSize: 10 }}>Bill To</div>
          <div style={{ fontWeight: 700, fontSize: 15, marginTop: 6 }}>{customer.company}</div>
          <div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 2 }}>{customer.contact}</div>
          {customer.emails?.[0] && <div style={{ fontSize: 12, color: "var(--text-2)" }}>{customer.emails[0]}</div>}
          {customer.country && <div style={{ fontSize: 12, color: "var(--text-2)" }}>{customer.country}</div>}
        </div>
        <div className="card-ledger" style={{ padding: 16 }}>
          <div className="overline" style={{ fontSize: 10 }}>Currency</div>
          <select value={currencyCode} onChange={(e) => setCurrencyCode(e.target.value)} className="input-base" style={{ marginTop: 6 }} data-testid="quote-currency">
            {currencies.map((c) => <option key={c.code} value={c.code}>{c.symbol} · {c.code}</option>)}
          </select>
        </div>
        <div className="card-ledger" style={{ padding: 16 }}>
          <div className="overline" style={{ fontSize: 10 }}>Date</div>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-base font-mono-data" style={{ marginTop: 6 }} data-testid="quote-date" />
        </div>
        <div className="card-ledger" style={{ padding: 16 }}>
          <div className="overline" style={{ fontSize: 10 }}>Valid Until</div>
          <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="input-base font-mono-data" style={{ marginTop: 6 }} data-testid="quote-valid-until" />
        </div>
      </div>

      <div className="card-ledger overflow-hidden mb-4" data-testid="quote-items-table">
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <colgroup>
            <col />
            <col style={{ width: 100 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 110 }} />
            {showExworks && <col style={{ width: 110 }} />}
            <col style={{ width: 36 }} />
          </colgroup>
          <thead>
            <tr style={{ background: "var(--surface-2)" }}>
              {["Product", "Unit", "Qty", `FOB (${sym})`, `CIF (${sym})`, ...(showExworks ? [`Ex-Works (${sym})`] : []), ""].map((h, i) => (
                <th key={i} className="overline" style={{ fontSize: 10, padding: "12px 10px", textAlign: i === 0 ? "left" : "center", borderBottom: "1px solid var(--border)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((row, idx) => (
              <tr key={idx} style={{ borderBottom: "1px solid var(--border)" }}>
                <td style={{ padding: "8px 10px" }}>
                  <input list="product-options" value={row.product} onChange={(e) => updateItem(idx, "product", e.target.value)} placeholder="Product" className="input-base" style={cellInput} data-testid={`quote-item-product-${idx}`} />
                </td>
                <td style={{ padding: "8px 6px" }}>
                  <select value={row.unit} onChange={(e) => updateItem(idx, "unit", e.target.value)} className="input-base" style={{ ...cellInput, textAlign: "center" }} data-testid={`quote-item-unit-${idx}`}>
                    {UNITS.map((u) => <option key={u}>{u}</option>)}
                  </select>
                </td>
                <td style={{ padding: "8px 6px" }}>
                  <input type="number" min={0} value={row.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} className="input-base font-mono-data no-spin" style={{ ...cellInput, textAlign: "center" }} data-testid={`quote-item-qty-${idx}`} />
                </td>
                {["fob_price", "cif_price", ...(showExworks ? ["ex_works_price"] : [])].map((pk) => (
                  <td key={pk} style={{ padding: "8px 6px" }}>
                    <input type="number" min={0} value={row[pk]} onChange={(e) => updateItem(idx, pk, e.target.value)} className="input-base font-mono-data no-spin" style={{ ...cellInput, textAlign: "right" }} data-testid={`quote-item-${pk}-${idx}`} />
                  </td>
                ))}
                <td style={{ textAlign: "center" }}>
                  <button onClick={() => removeItem(idx)} className="btn-ghost" style={{ padding: 4, color: "var(--danger)" }} data-testid={`quote-item-remove-${idx}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <datalist id="product-options">{products.map((p) => <option key={p.id} value={p.name} />)}</datalist>
        <button onClick={addItem} className="btn-ghost" style={{ width: "100%", justifyContent: "center", padding: 12, color: "var(--primary)", borderTop: "1px solid var(--border)", borderRadius: 0 }} data-testid="quote-add-item">
          <Plus size={14} /> Add line item
        </button>
      </div>

      {/* Totals — Incoterm subtotals; GST + grand total shown for INR */}
      <div className="flex justify-end gap-6 mb-4 flex-wrap" data-testid="quote-totals">
        {[
          ["FOB Total", fobTotal, true, true],
          ["CIF Total", cifTotal, false, true],
          ["Ex-Works Total", exwTotal, false, showExworks],
          ...(taxRate > 0 ? [[taxLabel || "GST", taxAmount, false, true]] : []),
          ...(taxRate > 0 ? [["Grand Total", grandTotal, true, true]] : []),
        ].filter(([, , , show]) => show).map(([label, val, primary]) => (
          <div key={label} style={{ textAlign: "right" }}>
            <div className="overline" style={{ fontSize: 10 }}>{label}</div>
            <div className="font-mono-data" style={{ fontSize: 18, fontWeight: 700, color: primary ? "var(--primary)" : "var(--text)", marginTop: 4 }}>{fmtMoney(val)}</div>
          </div>
        ))}
      </div>

      {/* Fixed terms + extra notes */}
      <div className="card-ledger mb-6" style={{ padding: 16 }} data-testid="quote-terms-card">
        <div className="overline" style={{ fontSize: 10, marginBottom: 10 }}>Terms & Conditions (fixed)</div>
        <ol style={{ paddingLeft: 22, margin: 0 }}>
          {fixedTerms.map((t, i) => (
            <li key={i} className="flex items-start justify-between gap-3" style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.6, marginBottom: 4 }} data-testid={`fixed-term-${i}`}>
              <span style={{ flex: 1 }}>{t}</span>
              <button onClick={() => removeFixedTerm(i)} className="btn-ghost" style={{ padding: 2, color: "var(--text-2)", flexShrink: 0 }} title="Remove this term" data-testid={`remove-term-${i}`}>
                <Trash2 size={12} />
              </button>
            </li>
          ))}
        </ol>
        <div className="overline" style={{ fontSize: 10, marginTop: 16, marginBottom: 6 }}>Payment Terms</div>
        <textarea value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} rows={2} className="input-base" style={{ resize: "vertical", fontFamily: "inherit" }} placeholder="e.g. 30% advance, balance against B/L copy" data-testid="quote-payment-terms" />

        <div className="overline" style={{ fontSize: 10, marginTop: 16, marginBottom: 6 }}>Additional Notes (optional)</div>
        <textarea value={extraNotes} onChange={(e) => setExtraNotes(e.target.value)} rows={3} className="input-base" style={{ resize: "vertical", fontFamily: "inherit" }} placeholder="Add custom terms — one per line" data-testid="quote-extra-notes" />

        <label className="flex items-center gap-2 mt-4" style={{ fontSize: 13, cursor: "pointer" }} data-testid="quote-show-exworks">
          <input type="checkbox" checked={showExworks} onChange={(e) => setShowExworks(e.target.checked)} />
          Show Ex-Works pricing on this quotation
        </label>
      </div>

      <div className="flex gap-3 justify-end">
        <button onClick={onClose} className="btn-outline" data-testid="quote-cancel">Cancel</button>
        <button onClick={saveOnly} disabled={saving} className="btn-outline" data-testid="quote-save-only">
          <Save size={15} /> {saving ? (isEdit ? "Updating…" : "Saving…") : (isEdit ? "Update" : "Save")}
        </button>
        <button onClick={saveAndEmail} disabled={saving} className="btn-outline" data-testid="quote-save-email">
          <Mail size={15} /> {saving ? (isEdit ? "Updating…" : "Saving…") : (isEdit ? "Update & Email" : "Save & Email")}
        </button>
        <button onClick={saveAndPrint} disabled={saving} className="btn-primary" data-testid="quote-save-print">
          <Printer size={15} /> {saving ? (isEdit ? "Updating…" : "Saving…") : (isEdit ? "Update & Print" : "Save & Print")}
        </button>
      </div>
    </div>
  );
}
